import { AfterContentInit, Component, Inject, OnInit } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialogRef as MatDialogRef,
} from "@angular/material/legacy-dialog";
import { colorToText, hexTextValidator, textToColor } from "@app/_helpers/color";
import { hasPermission } from "@app/_helpers/utils";
import { CustomValidators } from "@app/_validators/custom-validators";
import { AppService } from "@app/app.service";
import { TranslateService } from "@ngx-translate/core";
import { ClientsQuery, ClientsService, UserSettingsQuery } from "@timeghost/web-api";
import { BehaviorSubject } from "rxjs";
import { finalize, map } from "rxjs/operators";

@Component({
	selector: "app-create-client",
	templateUrl: "./create-client.component.html",
	styleUrls: ["./create-client.component.scss"],
})
export class CreateClientComponent implements OnInit, AfterContentInit {
	private readonly initialClientColor = this.appService.colorScheme.getColorFromScheme(
		this.clientQuery.getCount() || 1
	);
	csGroup: UntypedFormGroup = new UntypedFormGroup(
		{
			name: new UntypedFormControl("", [Validators.required, CustomValidators.minLength(3)]),
			description: new UntypedFormControl(""),
			color: new UntypedFormControl(this.initialClientColor),
			colorText: new UntypedFormControl(colorToText(this.initialClientColor), [
				Validators.maxLength(7),
				(ctrl) => hexTextValidator(ctrl?.value),
			]),
		},
		{
			validators: [
				({ root: ctrl }) => {
					const ctrls = (ctrl as UntypedFormGroup).controls;
					if (
						this.clientQuery.getCount(
							(x) => x.name?.toLowerCase() === `${ctrls.name.value}`.toLowerCase()
						) > 0
					) {
						ctrls.name.setErrors({ ...ctrls.name.errors, exists: true });
						return { exists: true };
					}
					return null;
				},
			],
			updateOn: "change",
		}
	);
	get csName() {
		return this.csGroup.controls.name;
	}
	get csDescription() {
		return this.csGroup.controls.description;
	}
	private _isLoadingAsync: BehaviorSubject<boolean> = new BehaviorSubject(false);
	isLoadingAsync = this._isLoadingAsync.asObservable();
	set isLoading(val: boolean) {
		this._isLoadingAsync.next(val);
	}

	readonly workspace$createPermission = this.userSettingsQuery.select().pipe(
		map((userSettings) => {
			return !!userSettings.workspace.permissionSettings?.groupsCanManageClients?.find((x) =>
				hasPermission(x.id, userSettings)
			);
		})
	);
	get csErrors() {
		if (this.csName.errors?.required) return { content: "errors.required", args: { field: "Name" } };
		if (this.csName.errors?.minlength) return { content: "errors.minlength", args: { field: "Name", length: 3 } };
		if (this.csName.errors?.exists) return { content: "errors.unique", args: { field: "Name" } };
		return null;
	}
	constructor(
		@Inject(MAT_DIALOG_DATA)
		private data: { name?: string; search?: string },
		private ref: MatDialogRef<CreateClientComponent>,
		private appService: AppService,
		private clientService: ClientsService,
		private translate: TranslateService,
		private userSettingsQuery: UserSettingsQuery,
		private clientQuery: ClientsQuery
	) {}
	readonly workspace$ = this.userSettingsQuery.select((x) => x.workspace);

	ngOnInit() {
		this.ref.updateSize("500px");
		this.ref.addPanelClass(["mat-dialog-vanilla", "mat-dialog-relative"]);
		if (this.data?.search) {
			this.csGroup.controls.name?.setValue(this.data.search);
		}
	}
	ngAfterContentInit() {
		this.csGroup.updateValueAndValidity();
		this.csGroup.controls.colorText.valueChanges.subscribe((txt: string) => {
			const parsed = textToColor(txt);
			if (parsed) this.csGroup.patchValue({ color: parsed }, { emitEvent: false });
		});
	}

	onColorPicked(color: string) {
		const text = colorToText(color);
		const parsed = textToColor(text);
		if (!parsed) return;
		this.csGroup.patchValue(
			{
				color: parsed,
				colorText: text,
			},
			{ emitEvent: false }
		);
		this.csGroup.updateValueAndValidity();
	}
	addEntry() {
		this.isLoading = true;
		this.ref.disableClose = true;
		const { colorText: _colorText, ...payload } = this.csGroup.value ?? {};
		this.clientService
			.add({
				...payload,
			})
			.pipe(finalize(() => (this.ref.disableClose = false)))
			.subscribe((x) => this.ref.close(x));
	}
}
